import { UserPurchaseApi, useAPIAction } from "melco-shared-logic";
import { useCallback, useState } from "react";
import { saveAs } from "file-saver";

/**
 * Downloads the invoice for a single purchase row.
 *
 * WHY the *Raw call, not the plain userPurchaseDownload convenience method:
 * only the raw response exposes `.headers`, and the backend hands back the
 * invoice's real filename via a header rather than in the JSON body (there
 * is no `filename` field on IdentityMelcoModelsUserPurchaseListItem) — same
 * approach as useDownloadReports.tsx for the reports CSV download.
 *
 * WHY an id-scoped loading state (isDownloadingForMelcoPaymentId) instead of
 * a single boolean: this is called from a table with one download icon per
 * row, so a single shared "isLoading" would spin every row's icon at once
 * when only one was clicked — same reasoning as
 * useResendInvite.ts's isResendingForUserId.
 */
export const useDownloadPurchaseInvoice = () => {
  const [isDownloadingForMelcoPaymentId, setIsDownloadingForMelcoPaymentId] =
    useState<string | undefined>();

  const [downloadInvoice] = useAPIAction(
    UserPurchaseApi,
    async (api, melcoPaymentId: string) => {
      setIsDownloadingForMelcoPaymentId(melcoPaymentId);

      try {
        const response = await api.userPurchaseDownloadRaw({
          melcoPaymentId,
        });
        const { raw } = response;
        const blob = await raw.blob();
        // GOTCHA: falls back to a generic name rather than leaving this
        // undefined — file-saver's saveAs() needs *some* filename, and an
        // extension-less/blank one leaves the browser to guess (often
        // producing a useless "download" file with no extension).
        const filename =
          raw.headers.get("x-filename") ?? `invoice-${melcoPaymentId}.pdf`;

        return {
          successMessages: [],
          data: { blob, filename },
        };
      } finally {
        setIsDownloadingForMelcoPaymentId(undefined);
      }
    },
    {
      translationPrefix: "purchases.actions.download",
    }
  );

  const downloadPurchaseInvoice = useCallback(
    async (melcoPaymentId: string) => {
      const result = await downloadInvoice(melcoPaymentId);
      if (result?.blob) {
        saveAs(result.blob, result.filename);
      }
    },
    [downloadInvoice]
  );

  return { downloadPurchaseInvoice, isDownloadingForMelcoPaymentId };
};
